// Accountant dashboard panel: the Team Roster — same numbers as the Team
// Performance page's roster (shared math in utils/teamRoster.js). Rows click
// through to Team Performance for the full drill-down view.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { formatCurrency } from "../../utils/helper";
import { rosterStats, rankRoster } from "../../utils/teamRoster";
import StatDealsModal from "../reuseable/StatDealsModal";

// Volume and deal counts follow the agent CREDITED with the deal; GCI follows
// the money, so it also covers slices earned on deals credited to a teammate.
// Stacked in one cell with nothing between them the pair reads like a
// commission rate, and it is not one: the halves are not the same deals.
const GCI_SCOPE_PENDING =
  "Every pending deal that pays this agent, including deals credited to a teammate. The volume above it covers only the deals credited to this agent, so the two do not divide into a commission rate.";
const PENDING_VOLUME_SCOPE =
  "Sale price of the pending deals credited to this agent.";

const initialsFor = (u) => {
  const f = (u?.firstname || "").trim();
  const l = (u?.lastname || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (f || l || "?").slice(0, 2).toUpperCase();
};

export default function TeamRosterPanel({ deals, users }) {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  // Same three rankings as the Team Performance roster, same shared math.
  const [mode, setMode] = useState("closed");
  // Row whose Deals number was clicked — opens the drill-down list.
  const [dealsFor, setDealsFor] = useState(null);

  const rows = useMemo(
    () => rankRoster(rosterStats(deals, users, year), { mode }),
    [deals, users, year, mode],
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-black/[0.06] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-slate-100 text-slate-700 rounded-md inline-flex items-center justify-center">
            <Users size={14} strokeWidth={2.2} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              Team Roster
            </div>
            <div className="text-[11.5px] text-gray-500">
              Click an agent for the full Team Performance view
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              Volume and deal counts follow the agent credited with each deal.
              Commission earned follows the money, so it also covers slices
              earned on teammates' deals.
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              The Command Center leaderboard ranks on the whole deal's gross
              commission instead, so the same two people can appear in a
              different order there.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label="Rank the roster by closed, pending, or both"
            className="hidden sm:inline-flex bg-slate-100 border border-slate-200 rounded-lg p-[3px] gap-[2px]"
          >
            {[
              { id: "closed", label: "Closed" },
              { id: "pending", label: "Pending" },
              { id: "total", label: "Closed + Pending" },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                aria-pressed={o.id === mode}
                onClick={() => setMode(o.id)}
                className={`px-2.5 py-1 rounded-md text-[12px] font-semibold transition ${
                  o.id === mode
                    ? "bg-[#1E293B] text-white"
                    : "text-gray-500 hover:text-slate-900"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-slate-700">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="p-1.5 rounded-lg hover:bg-gray-100"
              aria-label="Previous roster year"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-[14px] font-semibold w-12 text-center">
              {year}
            </span>
            <button
              onClick={() => setYear((y) => y + 1)}
              disabled={year >= currentYear}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
              aria-label="Next roster year"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-400 text-[13px]">
          No agents to show.
        </div>
      ) : (
        <div className="px-6 py-2">
          {/* items-start, not items-center: the commission column carries a
              second line explaining its basis, and the short labels beside it
              should stay on the top line rather than float to the middle. */}
          <div className="hidden sm:grid grid-cols-[28px_1.25fr_1fr_0.85fr_0.75fr_1fr_20px] gap-3 items-start py-2 border-b border-gray-200">
            <span></span>
            <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
              Agent
            </span>
            <span
              title="Sale price of the closed deals credited to this agent."
              className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold"
            >
              Closed Volume
            </span>
            <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
              Deals (All)
            </span>
            <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
              Pending
            </span>
            {/* This column is what the PERSON was paid, and the roster ranks
                on it. The Command Center leaderboard ranks on the whole
                deal's gross commission instead, so the two screens can order
                the same people differently. Both are right; the label has to
                say which question each one is answering. */}
            <span
              title="Every closed deal that paid this agent, including deals credited to a teammate. Not the commission on the Closed Volume column."
              className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold"
            >
              <span className="block">Commission Earned</span>
              <span className="block text-[9.5px] text-gray-400 normal-case tracking-normal font-normal leading-snug mt-0.5">
                What this agent was paid
              </span>
            </span>
            <span></span>
          </div>
          {rows.map((row) => (
            <div
              key={row.user.id}
              onClick={() => {
                // Don't navigate away when she's selecting a figure to copy.
                if (window.getSelection?.()?.toString()) return;
                navigate("/dashboard/team-management");
              }}
              className="grid grid-cols-2 sm:grid-cols-[28px_1.25fr_1fr_0.85fr_0.75fr_1fr_20px] gap-3 items-center py-2.5 border-b border-black/[0.04] last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors text-[13px]"
            >
              <span className="hidden sm:inline-flex w-7 h-7 rounded-full bg-slate-800 text-white text-[10px] font-bold items-center justify-center">
                {initialsFor(row.user)}
              </span>
              <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                <span className="text-gray-400 font-semibold tabular-nums">
                  {row.rank}
                </span>
                {row.user.firstname} {row.user.lastname}
                {row.delta !== 0 && (
                  <span
                    title={`${Math.abs(row.delta)} ${
                      row.delta > 0 ? "up" : "down"
                    } vs. the closed ranking`}
                    className={`text-[10px] font-bold px-1 py-[1px] rounded ${
                      row.delta > 0
                        ? "text-emerald-700 bg-emerald-50"
                        : "text-amber-700 bg-amber-50"
                    }`}
                  >
                    {row.delta > 0 ? "▲" : "▼"}
                    {Math.abs(row.delta)}
                  </span>
                )}
              </span>
              <span className="text-slate-700">
                {row.volume > 0 ? `$${row.volume.toLocaleString("en-US")}` : "—"}
              </span>
              <button
                type="button"
                title="See these deals"
                onClick={(e) => {
                  // The row navigates to Team Performance — the Deals number
                  // opens the list instead.
                  e.stopPropagation();
                  setDealsFor(row);
                }}
                className="hidden sm:block text-left -m-1.5 p-1.5 rounded-lg hover:bg-slate-100 transition"
              >
                <span className="block text-slate-700 font-medium underline decoration-slate-300 decoration-dotted underline-offset-2">
                  {row.dealsClosed + row.dealsActive}
                </span>
                <span className="block text-[11px] text-gray-500 leading-snug">
                  {row.dealsClosed} closed
                </span>
                <span className="block text-[11px] text-gray-500 leading-snug">
                  {row.dealsActive} pending
                </span>
              </button>
              {/* Pending sale volume, with the commission riding on it
                  underneath. Volume alone doesn't say what the agent actually
                  stands to earn. Same stacked shape as the Team Performance
                  roster, so the two pages read identically — and, like that
                  roster, the GCI line now says how far it reaches, because the
                  volume above it is only this agent's credited deals while the
                  commission covers every pending deal that pays them. The
                  amount is formatted like the GCI column beside it rather than
                  like the sale prices, so the two commission figures on one row
                  can't be read as different kinds of number. */}
              <div className="hidden sm:block">
                <span
                  title={PENDING_VOLUME_SCOPE}
                  className="block text-sky-700 font-medium"
                >
                  {row.pendingVolume > 0
                    ? `$${row.pendingVolume.toLocaleString("en-US")}`
                    : "—"}
                </span>
                {row.pendingComm > 0 && (
                  <>
                    <span
                      title={GCI_SCOPE_PENDING}
                      className="block text-[11px] text-gray-500 leading-snug"
                    >
                      {formatCurrency(row.pendingComm)} GCI
                    </span>
                    <span className="block text-[10px] text-gray-400 leading-snug">
                      across all pending deals
                    </span>
                  </>
                )}
              </div>
              <span className="text-slate-700">
                {row.gci > 0 ? formatCurrency(row.gci) : "—"}
              </span>
              <ChevronRight size={14} className="hidden sm:block text-gray-300" />
            </div>
          ))}
        </div>
      )}
      {dealsFor && (
        <StatDealsModal
          title={`${dealsFor.user.firstname} ${dealsFor.user.lastname} · Deals`}
          deals={[...dealsFor.closedDeals, ...dealsFor.pendingDeals]}
          amountOf={(d) => Number(d.offerPrice || 0)}
          onClose={() => setDealsFor(null)}
        />
      )}
    </div>
  );
}
